// src/components/MXMinimap.jsx
import React, { useEffect, useRef, useMemo, useCallback } from "react";

/**
 * Outline-style minimap that mirrors mxGraph Outline behavior.
 * - Expects diagramBounds {minX,minY,maxX,maxY} and current viewBox
 * - Click to center, drag the viewport rect to pan
 */
export default function MXMinimap({
  nodes = [],
  edges = [],
  diagramBounds = null,
  viewBox,
  onViewBoxChange,
  setActiveInteraction,
  showCriticalPath = false,
  timelineMode = false,
}) {
  const svgRef = useRef(null);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  
  // Create a map of nodes for quick lookup - always call hooks unconditionally
  const nodeMap = useMemo(() => {
    const map = new Map();
    nodes.forEach((node) => map.set(node.id, node));
    return map;
  }, [nodes]);

  // Add this to check if edge is critical
  const getEdgeColor = useCallback(
    (edge) => {
      const fromNode = nodeMap.get(edge.from);
      const toNode = nodeMap.get(edge.to);

      // Check if edge is critical (same logic as EdgeRenderer)
      const isCriticalEdge =
        showCriticalPath &&
        fromNode &&
        toNode &&
        fromNode.totalFloat === 0 &&
        toNode.totalFloat === 0;

      return isCriticalEdge ? "#FF0000" : "#333"; // Red for critical, dark gray for normal
    },
    [nodeMap, showCriticalPath]
  );

  useEffect(() => {
    const stop = () => {
      dragging.current = false;
      if (setActiveInteraction) setActiveInteraction(null);
    };
    window.addEventListener("mouseup", stop);
    return () => window.removeEventListener("mouseup", stop);
  }, [setActiveInteraction]);

  // Memoize the transformation calculations - always call hooks unconditionally
  const transformation = useMemo(() => {
    if (!diagramBounds) return null;

    const { minX, minY, maxX, maxY } = diagramBounds;
    const graphW = Math.max(1, maxX - minX);
    const graphH = Math.max(1, maxY - minY);

    const miniW = 180; // Reduced from 220 (-18%)
    const miniH = 100; // Reduced from 120 (-17%)
    const pad = 6; // Reduced from 8 (-25%, but keep proportional)

    const scaleX = (miniW - pad * 2) / graphW;
    const scaleY = (miniH - pad * 2) / graphH;
    const miniScale = Math.min(scaleX, scaleY);

    return {
      miniW,
      miniH,
      pad,
      minX,
      minY,
      miniScale,
      toMiniX: (gx) => (gx - minX) * miniScale + pad,
      toMiniY: (gy) => (gy - minY) * miniScale + pad,
      toGraphX: (mx) => (mx - pad) / miniScale + minX,
      toGraphY: (my) => (my - pad) / miniScale + minY,
    };
  }, [diagramBounds]);

  // start drag of viewport rect
  const onStartDrag = useCallback(
    (e) => {
      if (e.button !== 0 || !transformation) return;

      dragging.current = true;
      if (setActiveInteraction) setActiveInteraction("minimap");

      const rect = svgRef.current.getBoundingClientRect();
      const viewX = transformation.toMiniX(viewBox.x);
      const viewY = transformation.toMiniY(viewBox.y);

      dragOffset.current = {
        x: e.clientX - rect.left - viewX,
        y: e.clientY - rect.top - viewY,
      };

      e.stopPropagation();
      e.preventDefault();
    },
    [transformation, viewBox, setActiveInteraction]
  );

  // perform dragging: map new mini rect pos => graph center => viewBox x/y
  const onMove = useCallback(
    (e) => {
      if (!dragging.current || !transformation) return;

      const rect = svgRef.current.getBoundingClientRect();
      const mx = e.clientX - rect.left - dragOffset.current.x;
      const my = e.clientY - rect.top - dragOffset.current.y;

      const viewW = Math.max(4, viewBox.w * transformation.miniScale);
      const viewH = Math.max(4, viewBox.h * transformation.miniScale);

      const cx = Math.max(
        transformation.pad,
        Math.min(mx, transformation.miniW - transformation.pad - viewW)
      );
      const cy = Math.max(
        transformation.pad,
        Math.min(my, transformation.miniH - transformation.pad - viewH)
      );

      const graphCenterX = transformation.toGraphX(cx + viewW / 2);
      const graphCenterY = transformation.toGraphY(cy + viewH / 2);

      if (onViewBoxChange) {
        onViewBoxChange((prev) => ({
          ...prev,
          x: graphCenterX - prev.w / 2,
          y: graphCenterY - prev.h / 2,
        }));
      }
    },
    [transformation, viewBox, onViewBoxChange]
  );

  const onMiniClick = useCallback(
    (e) => {
      if (dragging.current || !transformation) return;

      const rect = svgRef.current.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      const graphCenterX = transformation.toGraphX(mx);
      const graphCenterY = transformation.toGraphY(my);

      if (onViewBoxChange) {
        onViewBoxChange((prev) => ({
          ...prev,
          x: graphCenterX - prev.w / 2,
          y: graphCenterY - prev.h / 2,
        }));
      }
    },
    [transformation, onViewBoxChange]
  );

  // Early return must be AFTER all hooks
  if (!nodes || nodes.length === 0 || !diagramBounds || !transformation)
    return null;

  const { miniW, miniH, toMiniX, toMiniY, miniScale } = transformation;

  // viewport rectangle inside minimap
  const viewX = toMiniX(viewBox.x);
  const viewY = toMiniY(viewBox.y);
  const viewW = Math.max(4, viewBox.w * miniScale);
  const viewH = Math.max(4, viewBox.h * miniScale);

  return (
    <div
      style={{
        position: "fixed",
        bottom: 16.5,
        right: 16.5,
        width: miniW,
        height: miniH,
        zIndex: 3000,
        background: "rgba(255, 255, 255, 0.95)",
        border: "1px solid #aaa",
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      }}
    >
      <svg
        ref={svgRef}
        width={miniW}
        height={miniH}
        onMouseDown={onMiniClick}
        onMouseMove={onMove}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {/* Render edges first (behind nodes) */}
        {!timelineMode && edges.map((edge) => {
          const A = nodeMap.get(edge.from);
          const B = nodeMap.get(edge.to);

          if (!A || !B) return null;

          // Get edge color
          const edgeColor = getEdgeColor(edge);
          const isCriticalEdge = edgeColor === "#FF0000";
          const strokeWidth = isCriticalEdge
            ? 6.5 * miniScale
            : 5.5 * miniScale;

          // Simple orthogonal-like path (right angle)
          const srcX = A.x + (A.width || 220);
          const srcY = A.y + (A.height || 85) / 2;
          const dstX = B.x;
          const dstY = B.y + (B.height || 85) / 2;

          const midX = (srcX + dstX) / 2;

          return (
            <path
              key={`edge-${edge.from}-${edge.to}`}
              d={`
                M ${toMiniX(srcX)} ${toMiniY(srcY)}
                L ${toMiniX(midX)} ${toMiniY(srcY)}
                L ${toMiniX(midX)} ${toMiniY(dstY)}
                L ${toMiniX(dstX)} ${toMiniY(dstY)}
              `}
              fill="none"
              stroke={edgeColor}
              strokeWidth={strokeWidth}
              strokeOpacity="0.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}
        
        {/* Render nodes */}
        {nodes.map((node) => (
          <rect
            key={`node-${node.id}`}
            x={toMiniX(node.x)}
            y={toMiniY(node.y)}
            width={(node.width || 220) * miniScale}
            height={(node.height || 85) * miniScale}
            fill="#1976d2"
            stroke="white"
            strokeWidth="0.5"
            rx="2"
          />
        ))}
        
        {/* Viewport rectangle - draggable */}
        <rect
          x={viewX}
          y={viewY}
          width={viewW}
          height={viewH}
          fill="rgba(255, 0, 0, 0.1)"
          stroke="red"
          strokeWidth="2"
          onMouseDown={onStartDrag}
          style={{ cursor: dragging.current ? "grabbing" : "grab" }}
        />
      </svg>

      {/* Optional: Add a subtle title */}
      <div
        style={{
          position: "absolute",
          top: 4,
          left: 4,
          fontSize: "10px",
          color: "#666",
          fontWeight: "bold",
          pointerEvents: "none",
        }}
      ></div>
    </div>
  );
}